import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  HStack,
  Input,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import { Check, X } from 'lucide-react';
import { Icon } from '@chakra-ui/react';
import { Milestone } from '../../types';
import { formatDateForInput } from '../../../../utils/date.utils';

interface EditMilestoneFormProps {
  milestone: Milestone;
  onUpdate: (milestoneId: string, task: string, dueDate: string) => Promise<void>;
  onSuccess?: () => void;
  onCancel?: () => void;
}

/**
 * Inline form for editing an existing milestone
 * Uses Formik for form state and Yup for validation
 */
export function EditMilestoneForm({
  milestone,
  onUpdate,
  onSuccess,
  onCancel,
}: EditMilestoneFormProps) {
  const toast = useToast();

  // Color mode values for dark mode support
  const formBg = useColorModeValue('blue.50', 'blue.900');
  const inputBg = useColorModeValue('white', 'gray.700');

  const validationSchema = Yup.object({
    task: Yup.string()
      .required('Task is required')
      .min(3, 'Task must be at least 3 characters'),
    dueDate: Yup.date().required('Due date is required'),
  });

  const handleSubmit = async (
    values: { task: string; dueDate: string },
    { setSubmitting }: any
  ) => {
    try {
      await onUpdate(milestone.id, values.task, values.dueDate);
      toast({
        title: 'Milestone updated',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update milestone',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box p={3} bg={formBg} borderRadius="md" mb={2}>
      <Formik
        initialValues={{
          task: milestone.task,
          dueDate: formatDateForInput(milestone.dueDate),
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <HStack spacing={2} align="flex-start">
              {onCancel && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onCancel}
                  aria-label="Cancel"
                  isDisabled={isSubmitting}
                >
                  <Icon as={X} w={4} h={4} />
                </Button>
              )}
              <Button
                type="submit"
                size="sm"
                variant="ghost"
                colorScheme="green"
                isLoading={isSubmitting}
                aria-label="Save"
              >
                <Icon as={Check} w={4} h={4} />
              </Button>

              <Field name="task">
                {({ field, form }: any) => (
                  <FormControl isInvalid={form.errors.task && form.touched.task} flex={2}>
                    <Input
                      {...field}
                      placeholder="Milestone task"
                      size="sm"
                      bg={inputBg}
                    />
                    <FormErrorMessage fontSize="xs">{form.errors.task}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>

              <Field name="dueDate">
                {({ field, form }: any) => (
                  <FormControl isInvalid={form.errors.dueDate && form.touched.dueDate} flex={1}>
                    <Input {...field} type="date" size="sm" bg={inputBg} placeholder="Due date" />
                    <FormErrorMessage fontSize="xs">{form.errors.dueDate}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>
            </HStack>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
